/**
 * BulkActivateDeactivateBar
 *
 * Floating pill surfaced at the bottom of the products table on the
 * **Published** tab when the user has selected at least one row (GRAIN-1521).
 * Sibling to `BulkPublishBar` — shares the same animated `BulkActionBar`
 * chrome so both tabs read as one system.
 *
 * ## State machine
 *
 * | mode       | content                                          | parent owns            |
 * |------------|--------------------------------------------------|------------------------|
 * | `selected` | "N products selected" + Activate + Deactivate    | selection, callbacks   |
 * | `loading`  | spinner + "Queuing N products"                   | mutation pending state |
 * | `queued`   | check + "N products queued" + dismiss + timer    | auto-dismiss + link    |
 * | `error`    | red × + "Failed to queue N products" + dismiss   | explicit dismiss only  |
 *
 * No action-specific verbs in the copy — activate and deactivate both
 * enqueue async jobs, so the user sees the same queue copy either way.
 * The `loading` copy says "Queuing", not "Activating/Deactivating",
 * because the HTTP request only puts a job on the queue; the real work
 * happens asynchronously.
 *
 * ## Scope
 *
 * - **Frontend only.** Real mutations land with GRAIN-1524 / GRAIN-1525.
 * - **No i18n strings here.** Copy comes from the `products.bulk_bar.*`
 *   scope via `useTranslation`.
 */

// Libraries
import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { motion } from 'framer-motion';

// Local components
import {
  BulkActionBar,
  CountedText,
  DismissProgressBar,
  FadeIn,
  preserveEdgeWhitespace,
  splitAroundCount,
  splitTrailingWord,
} from '@/components/ui/bulk-action-bar';

// Icons
import { LoadingSpinnerIcon } from '@icons/LoadingSpinnerIcon';
import { CancelledIcon } from '@icons/CancelledIcon';
import { CancelIcon } from '@icons/primitives/CancelIcon';
import { ConfirmedIcon } from '@icons/primitives/ConfirmedIcon';

// Hooks and utilities
import { AnimatedCount } from '@/components/ui/AnimatedCount';
import { cn } from '@/lib/utils';
import { useTranslation } from '@hooks/useTranslation';
import { interpolateCount, pluralizeKey } from '@/react/utils/i18n';


const LOADING_ICON = <LoadingSpinnerIcon className="size-5 shrink-0 text-off-black/60" />;
const QUEUED_ICON = <ConfirmedIcon className="size-5 shrink-0 text-yield-positive" aria-hidden="true" />;
const ERROR_ICON = <CancelledIcon className="size-5 shrink-0 text-chilli-red" aria-hidden="true" />;

// Figma 10261-160794: outlined pill with a coloured border + matching
// text colour. Activate maps to yield-positive (#31aa88, the Figma
// "mint-green"), Deactivate to chilli-red. Shared base keeps shape and
// typography in lockstep.
const OUTLINE_BUTTON_BASE =
  'rounded-xs border px-4 py-2 text-sm font-semibold leading-[1.5] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
const ACTIVATE_BUTTON = 'border-yield-positive text-yield-positive hover:bg-yield-positive hover:text-off-white';
const DEACTIVATE_BUTTON = 'border-chilli-red text-chilli-red hover:bg-chilli-red hover:text-off-white';


// The shell's `SHAPE_SPRING` has a subtle bounce which reads as character on
// the pill-width morph (mode swaps, count width). For the buttons' own
// slide/collapse on selection-state change we don't want them to overshoot
// — a critically-damped spring (`bounce: 0`) still decelerates naturally
// but settles without wiggle.
// Both buttons stay mounted; the hidden one collapses its width to 0
// inside an `overflow-hidden` wrapper. Because no button ever changes
// flex position while the other is materializing, there's no layout-
// sliding overlap window. Blur + scale + fade handle the visual morph.
const BUTTON_HIDDEN = { opacity: 0, scale: 0.8, filter: 'blur(8px)' };
const BUTTON_VISIBLE = { opacity: 1, scale: 1, filter: 'blur(0px)' };
const BUTTON_TRANSITION = { duration: 0.25, ease: 'easeOut' as const };

export type BulkActivateDeactivateBarMode = 'selected' | 'loading' | 'queued' | 'error';

export type BulkActivateDeactivateSelectionState = 'all_active' | 'all_inactive' | 'mixed';

export interface BulkActivateDeactivateBarProps {
  /** Number shown inside the pill. Parent should freeze this at submit time
   *  so the queued/error copy keeps its count if the selection mutates
   *  while the bar is on screen. */
  selectedCount: number;

  /** Which of the four visual states to render. Defaults to `selected`. */
  mode?: BulkActivateDeactivateBarMode;

  /** Active/inactive composition of the current selection. Gates which of
   *  Activate / Deactivate render in `selected` mode. Defaults to `mixed`
   *  (both buttons). */
  selectionState?: BulkActivateDeactivateSelectionState;

  /** Fires when the Activate button is clicked (selected mode only). */
  onActivate: () => void;

  /** Fires when the Deactivate button is clicked (selected mode only). */
  onDeactivate: () => void;

  /** Fires when the user clicks the dismiss (×) button (queued mode only). */
  onDismissQueued?: () => void;

  /** Fires when the user clicks the dismiss (×) button (error mode only). */
  onDismissError?: () => void;

  /** Fires when the queued progress bar reaches 100% — parent should
   *  unmount the bar. Error mode has no auto-dismiss. */
  onAutoDismiss?: () => void;

  /** Fires when the underlined "queued" word is clicked. When provided,
   *  the word renders as a real `<button>`. */
  onQueuedLinkClick?: () => void;

  /** Lifetime of the queued toast in milliseconds. */
  dismissDurationMs?: number;

  /** Extra classes merged onto the outer shell. */
  className?: string;
}

export function BulkActivateDeactivateBar({
  selectedCount,
  mode = 'selected',
  selectionState = 'mixed',
  onActivate,
  onDeactivate,
  onDismissQueued,
  onDismissError,
  onAutoDismiss,
  onQueuedLinkClick,
  dismissDurationMs,
  className,
}: BulkActivateDeactivateBarProps) {
  const { t } = useTranslation();
  const selectedTemplate = t(pluralizeKey('bulk_bar.products_selected', selectedCount));
  // aria-label is a plain string; interpolate %{count} so screen readers
  // announce the real number instead of the raw token.
  const ariaLabel = interpolateCount(
    mode === 'selected' ? selectedTemplate
    : mode === 'loading' ? t('bulk_bar.products_queuing')
    : mode === 'queued' ? t('bulk_bar.products_queued')
    : t('bulk_bar.products_error'),
    selectedCount,
  );

  return (
    <BulkActionBar ariaLabel={ariaLabel} className={className}>
      <FadeIn keyId={mode}>
        {mode === 'selected' && (
          <SelectedContent
            count={selectedCount}
            template={selectedTemplate}
            activateLabel={t('bulk_bar.activate')}
            deactivateLabel={t('bulk_bar.deactivate')}
            selectionState={selectionState}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
          />
        )}
        {mode === 'loading' && (
          <LoadingContent count={selectedCount} template={t('bulk_bar.products_queuing')} />
        )}
        {mode === 'queued' && (
          <QueuedContent
            count={selectedCount}
            template={t('bulk_bar.products_queued')}
            dismissLabel={t('bulk_bar.dismiss')}
            onDismiss={onDismissQueued}
            onLinkClick={onQueuedLinkClick}
          />
        )}
        {mode === 'error' && (
          <ErrorContent
            count={selectedCount}
            template={t('bulk_bar.products_error')}
            dismissLabel={t('bulk_bar.dismiss')}
            onDismiss={onDismissError}
          />
        )}
      </FadeIn>

      {mode === 'queued' && (
        <DismissProgressBar durationMs={dismissDurationMs} onComplete={onAutoDismiss} />
      )}
    </BulkActionBar>
  );
}

interface SelectedContentProps {
  count: number;
  template: string;
  activateLabel: string;
  deactivateLabel: string;
  selectionState: BulkActivateDeactivateSelectionState;
  onActivate: () => void;
  onDeactivate: () => void;
}

function SelectedContent({
  count,
  template,
  activateLabel,
  deactivateLabel,
  selectionState,
  onActivate,
  onDeactivate,
}: SelectedContentProps) {
  const showActivate = selectionState !== 'all_active';
  const showDeactivate = selectionState !== 'all_inactive';
  // Both buttons are always mounted. Each sits inside a width-animating
  // wrapper with `overflow-hidden`, so the hidden button's footprint
  // collapses to zero while the visible one stays put. Neither button
  // ever shifts position — the pill width morphs via the shell's layout
  // spring — which eliminates the sliding-overlap problem that earlier
  // `AnimatePresence popLayout` approaches exposed.
  //
  // `layout="position"` on the count keeps its intrinsic size fixed
  // against the shell's width spring (prevents horizontal stretch).
  return (
    <div className="flex items-center gap-8">
      {/* Reserve width for the longest plausible count copy ("999 products
          selected" at 14px semibold Inter ≈ 147px). Combined with
          tabular-nums, this keeps the buttons stationary whether the
          selection is 1 or 999, singular or plural. */}
      <motion.span
        layout="position"
        className="inline-block min-w-[148px] tabular-nums"
      >
        <CountedText count={count} template={template} />
      </motion.span>
      {/* No flex gap here — the inter-button spacing lives on Activate's
          wrapper as a conditional `marginRight`, so it collapses alongside
          the wrapper's width when the sibling is hidden. */}
      <div className="flex items-center">
        <MorphButton
          show={showActivate}
          trailingGap={showDeactivate}
          onClick={onActivate}
          className={cn(OUTLINE_BUTTON_BASE, ACTIVATE_BUTTON)}
        >
          {activateLabel}
        </MorphButton>
        <MorphButton
          show={showDeactivate}
          onClick={onDeactivate}
          className={cn(OUTLINE_BUTTON_BASE, DEACTIVATE_BUTTON)}
        >
          {deactivateLabel}
        </MorphButton>
      </div>
    </div>
  );
}

interface MorphButtonProps {
  show: boolean;
  onClick: () => void;
  className: string;
  children: React.ReactNode;
  /** Render a 12px trailing margin that collapses with the wrapper when
   *  hidden — reserves inter-button space only while a sibling is visible. */
  trailingGap?: boolean;
}

function MorphButton({ show, trailingGap, onClick, className, children }: MorphButtonProps) {
  return (
    <motion.div
      animate={{
        width: show ? 'auto' : 0,
        marginRight: show && trailingGap ? 12 : 0,
      }}
      initial={false}
      transition={BUTTON_TRANSITION}
      className="shrink-0 overflow-hidden"
    >
      <motion.button
        type="button"
        onClick={onClick}
        animate={show ? BUTTON_VISIBLE : BUTTON_HIDDEN}
        initial={false}
        transition={BUTTON_TRANSITION}
        style={{ pointerEvents: show ? 'auto' : 'none' }}
        aria-hidden={show ? undefined : true}
        tabIndex={show ? undefined : -1}
        className={className}
      >
        {children}
      </motion.button>
    </motion.div>
  );
}

function LoadingContent({ count, template }: { count: number; template: string }) {
  return (
    <span className="flex items-center gap-2">
      {LOADING_ICON}
      <CountedText count={count} template={template} />
    </span>
  );
}

interface QueuedContentProps {
  count: number;
  template: string;
  dismissLabel: string;
  onDismiss?: () => void;
  onLinkClick?: () => void;
}

function QueuedContent({ count, template, dismissLabel, onDismiss, onLinkClick }: QueuedContentProps) {
  const [pre, postRaw] = splitAroundCount(template);
  const [middle, suffix] = splitTrailingWord(postRaw);
  return (
    <div className="flex items-center gap-4">
      <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold leading-[1.5] text-off-black">
        {QUEUED_ICON}
        <span className="inline-flex items-center">
          {pre && <span>{preserveEdgeWhitespace(pre)}</span>}
          <AnimatedCount value={count} />
          {middle && <span>{preserveEdgeWhitespace(middle)}</span>}
          {onLinkClick ? (
            <button
              type="button"
              onClick={onLinkClick}
              className={cn('cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring', UNDERLINE_STATIC)}
            >
              {suffix}
            </button>
          ) : (
            <span className={cn('cursor-pointer', UNDERLINE_STATIC)}>{suffix}</span>
          )}
        </span>
      </span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="flex size-5 shrink-0 items-center justify-center rounded-xs p-1 text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <CancelIcon className="size-3" />
        </button>
      )}
    </div>
  );
}

interface ErrorContentProps {
  count: number;
  template: string;
  dismissLabel: string;
  onDismiss?: () => void;
}

function ErrorContent({ count, template, dismissLabel, onDismiss }: ErrorContentProps) {
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex items-center gap-[6px]">
        {ERROR_ICON}
        <CountedText count={count} template={template} />
      </span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="flex size-5 shrink-0 items-center justify-center rounded-xs p-1 text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <CancelIcon className="size-3" />
        </button>
      )}
    </div>
  );
}
